/**
 * User Preferences - Google Drive Storage
 *
 * Manages user preferences stored in Google Drive.
 * Structure: NIDOnote/preferences/user-preferences.json
 *
 * This replaces the Supabase device_preferences table for theme and other settings.
 */

import { driveClient } from './google-drive';
import type { DriveFolder } from './google-drive';

/**
 * Device-specific preferences
 * Each device (web browser, mobile app, etc.) can have its own preferences
 */
export interface DevicePreferences {
  deviceId: string;
  deviceName: string;
  webTheme?: 'light' | 'dark';
  mobileTheme?: 'light' | 'dark';
  lastUpdated: string;
}

/**
 * Global preferences that apply across all devices
 */
export interface GlobalPreferences {
  autoSaveNotes: boolean;
  autoSaveEvents: boolean;
  defaultView: 'notes' | 'calendar' | 'dashboard';
  defaultSpaceId?: string;
  notificationsEnabled?: boolean;
}

/**
 * Complete user preferences structure
 */
export interface UserPreferences {
  version: string; // Schema version for future migrations
  devices: {
    [deviceId: string]: DevicePreferences;
  };
  global: GlobalPreferences;
}

/**
 * Default preferences for new users
 */
const DEFAULT_PREFERENCES: UserPreferences = {
  version: '1.0',
  devices: {},
  global: {
    autoSaveNotes: true,
    autoSaveEvents: true,
    defaultView: 'dashboard',
    notificationsEnabled: true,
  },
};

class PreferencesDriveService {
  private preferencesFolder: DriveFolder | null = null;
  private readonly PREFERENCES_FILE = 'user-preferences.json';

  /**
   * Initialize the preferences folder structure
   * Structure: NIDOnote/preferences/
   */
  private async initializePreferencesFolder(): Promise<DriveFolder> {
    if (this.preferencesFolder) {
      return this.preferencesFolder;
    }

    // Get or create root NIDOnote folder
    const rootFolder = await driveClient.getOrCreateFolder('NIDOnote');

    // Create preferences folder inside NIDOnote
    this.preferencesFolder = await driveClient.getOrCreateFolder(
      'preferences',
      rootFolder.id
    );

    return this.preferencesFolder;
  }

  /**
   * Load user preferences from Google Drive
   */
  async loadPreferences(): Promise<UserPreferences> {
    try {
      console.log('📥 Loading preferences from Google Drive...');

      const folder = await this.initializePreferencesFolder();

      // Find the preferences file
      const file = await driveClient.findFile(this.PREFERENCES_FILE, folder.id);

      if (!file) {
        console.log('ℹ️ No preferences file found, using defaults');
        return DEFAULT_PREFERENCES;
      }

      // Download and parse the file
      const content = await driveClient.downloadFile(file.id);
      const preferences = JSON.parse(content) as UserPreferences;

      console.log('✅ Preferences loaded from Google Drive:', preferences);
      return preferences;
    } catch (error) {
      console.error('❌ Error loading preferences from Google Drive:', error);
      // Return defaults if loading fails
      return DEFAULT_PREFERENCES;
    }
  }

  /**
   * Save user preferences to Google Drive
   */
  async savePreferences(preferences: UserPreferences): Promise<void> {
    try {
      console.log('💾 Saving preferences to Google Drive...', preferences);

      const folder = await this.initializePreferencesFolder();

      // Check if file already exists
      const existingFile = await driveClient.findFile(this.PREFERENCES_FILE, folder.id);

      if (existingFile) {
        // Update existing file
        await driveClient.updateFile(existingFile.id, preferences);
        console.log('✅ Preferences updated in Google Drive');
      } else {
        // Create new file
        await driveClient.uploadFile(
          this.PREFERENCES_FILE,
          preferences,
          folder.id,
          'application/json'
        );
        console.log('✅ Preferences created in Google Drive');
      }
    } catch (error) {
      console.error('❌ Error saving preferences to Google Drive:', error);
      throw error;
    }
  }

  /**
   * Get device-specific preferences
   */
  async getDevicePreferences(deviceId: string): Promise<DevicePreferences | null> {
    const preferences = await this.loadPreferences();
    return preferences.devices[deviceId] || null;
  }

  /**
   * Get unique device name by adding number if duplicate exists
   */
  private getUniqueDeviceName(deviceId: string, baseName: string, devices: { [id: string]: DevicePreferences }): string {
    // If this device already has a name, keep it
    if (devices[deviceId]?.deviceName) {
      return devices[deviceId].deviceName;
    }

    // Check if base name is already taken by another device
    const existingNames = Object.entries(devices)
      .filter(([id]) => id !== deviceId)
      .map(([_, device]) => device.deviceName);

    if (!existingNames.includes(baseName)) {
      return baseName; // Base name is unique
    }

    // Base name exists, find the next available number
    let counter = 2;
    while (existingNames.includes(`${baseName} (${counter})`)) {
      counter++;
    }

    const uniqueName = `${baseName} (${counter})`;
    console.log(`ℹ️ Device name '${baseName}' already exists, using '${uniqueName}'`);
    return uniqueName;
  }

  /**
   * Update device-specific preferences
   */
  async updateDevicePreferences(
    deviceId: string,
    deviceName: string,
    updates: Partial<Omit<DevicePreferences, 'deviceId' | 'deviceName' | 'lastUpdated'>>
  ): Promise<void> {
    const preferences = await this.loadPreferences();

    // Get unique device name (adds number if duplicate)
    const uniqueDeviceName = this.getUniqueDeviceName(deviceId, deviceName, preferences.devices);

    // Get existing device preferences or create new
    const existingDevice = preferences.devices[deviceId] || {
      deviceId,
      deviceName: uniqueDeviceName,
      lastUpdated: new Date().toISOString(),
    };

    // Merge updates
    preferences.devices[deviceId] = {
      ...existingDevice,
      ...updates,
      deviceId, // Ensure these don't get overwritten
      deviceName: uniqueDeviceName,
      lastUpdated: new Date().toISOString(),
    };

    await this.savePreferences(preferences);
  }

  /**
   * Update web theme for a device
   */
  async updateWebTheme(
    deviceId: string,
    deviceName: string,
    theme: 'light' | 'dark'
  ): Promise<void> {
    await this.updateDevicePreferences(deviceId, deviceName, { webTheme: theme });
  }

  /**
   * Update mobile theme for a device
   */
  async updateMobileTheme(
    deviceId: string,
    deviceName: string,
    theme: 'light' | 'dark'
  ): Promise<void> {
    await this.updateDevicePreferences(deviceId, deviceName, { mobileTheme: theme });
  }

  /**
   * Get web theme for a device
   * If device not found, tries to inherit from similar device (same deviceName)
   */
  async getWebTheme(deviceId: string): Promise<'light' | 'dark' | null> {
    const device = await this.getDevicePreferences(deviceId);

    if (device?.webTheme) {
      return device.webTheme;
    }

    // Device not found or no theme set - try to inherit from similar device
    const preferences = await this.loadPreferences();
    const devices = Object.values(preferences.devices);

    if (devices.length === 0) {
      return null; // No devices at all
    }

    // Try to find a device with the same deviceName (e.g., "Windows - Chrome")
    const currentDeviceName = device?.deviceName;
    if (currentDeviceName) {
      const similarDevice = devices.find(
        d => d.deviceId !== deviceId &&
        d.deviceName === currentDeviceName &&
        d.webTheme
      );

      if (similarDevice?.webTheme) {
        console.log(`ℹ️ Inheriting web theme from similar device (${currentDeviceName}):`, similarDevice.webTheme);
        return similarDevice.webTheme;
      }
    }

    // No similar device found - try to inherit from any device with a web theme
    const anyDeviceWithTheme = devices.find(d => d.webTheme);
    if (anyDeviceWithTheme?.webTheme) {
      console.log(`ℹ️ Inheriting web theme from first available device:`, anyDeviceWithTheme.webTheme);
      return anyDeviceWithTheme.webTheme;
    }

    return null;
  }

  /**
   * Get mobile theme for a device
   * If device not found, tries to inherit from similar device (same deviceName)
   */
  async getMobileTheme(deviceId: string): Promise<'light' | 'dark' | null> {
    const device = await this.getDevicePreferences(deviceId);

    if (device?.mobileTheme) {
      return device.mobileTheme;
    }

    // Device not found or no theme set - try to inherit from similar device
    const preferences = await this.loadPreferences();
    const devices = Object.values(preferences.devices);

    if (devices.length === 0) {
      return null; // No devices at all
    }

    // Try to find a device with the same deviceName (e.g., "Android - NIDOnote")
    const currentDeviceName = device?.deviceName;
    if (currentDeviceName) {
      const similarDevice = devices.find(
        d => d.deviceId !== deviceId &&
        d.deviceName === currentDeviceName &&
        d.mobileTheme
      );

      if (similarDevice?.mobileTheme) {
        console.log(`ℹ️ Inheriting mobile theme from similar device (${currentDeviceName}):`, similarDevice.mobileTheme);
        return similarDevice.mobileTheme;
      }
    }

    // No similar device found - try to inherit from any device with a mobile theme
    const anyDeviceWithTheme = devices.find(d => d.mobileTheme);
    if (anyDeviceWithTheme?.mobileTheme) {
      console.log(`ℹ️ Inheriting mobile theme from first available device:`, anyDeviceWithTheme.mobileTheme);
      return anyDeviceWithTheme.mobileTheme;
    }

    return null;
  }

  /**
   * List all devices for debugging/management
   */
  async listDevices(): Promise<DevicePreferences[]> {
    const preferences = await this.loadPreferences();
    return Object.values(preferences.devices);
  }

  /**
   * Delete a device from preferences
   */
  async deleteDevice(deviceId: string): Promise<void> {
    const preferences = await this.loadPreferences();
    delete preferences.devices[deviceId];
    await this.savePreferences(preferences);
  }

  /**
   * Get global preferences
   */
  async getGlobalPreferences(): Promise<GlobalPreferences> {
    const preferences = await this.loadPreferences();
    return preferences.global;
  }

  /**
   * Update global preferences
   */
  async updateGlobalPreferences(updates: Partial<GlobalPreferences>): Promise<void> {
    const preferences = await this.loadPreferences();
    preferences.global = {
      ...preferences.global,
      ...updates,
    };
    await this.savePreferences(preferences);
  }

  /**
   * Get a single global preference
   */
  async getGlobalPreference<K extends keyof GlobalPreferences>(
    key: K
  ): Promise<GlobalPreferences[K]> {
    const global = await this.getGlobalPreferences();
    return global[key];
  }

  /**
   * Set a single global preference
   */
  async setGlobalPreference<K extends keyof GlobalPreferences>(
    key: K,
    value: GlobalPreferences[K]
  ): Promise<void> {
    await this.updateGlobalPreferences({ [key]: value } as Partial<GlobalPreferences>);
  }
}

// Export singleton instance
export const preferencesDriveService = new PreferencesDriveService();
